// One hit-test per mouse move for every hover-sensitive layer on the map.
//
// MapLibre's layer-scoped listeners (`map.on('mouseenter', layerId, …)`) are
// sugar over a plain `mousemove` that calls queryRenderedFeatures for THAT
// layer on every event — and a `mouseleave` registers a second one. The cockpit
// had a pointer-cursor pair on each of eleven clickable layers plus the
// aircraft, FIRMS, dome and rotate-grip overlays: two dozen feature queries
// per mouse move, each walking the feature index of every tile under the
// cursor, which is why moving the mouse across a dense map felt like dragging
// through mud.
//
// This module replaces all of them with ONE query per animation frame over the
// union of registered layers, then tells each registration whether it is
// hovered. Behaviour is the same as the delegated listeners it replaces, with
// one deliberate improvement: the cursor is only touched when it is ours to
// touch, so a draw tool's crosshair survives the mouse crossing a marker.

import type * as maplibregl from 'maplibre-gl';
import { isMapEditing } from './editlock';

export interface HoverSpec {
  /** Layers this registration cares about; missing ones are skipped. */
  layers: readonly string[];
  /** CSS cursor while over one of `layers`. */
  cursor?: string;
  /** Registration is inert while this returns false (a drag in progress). */
  when?: () => boolean;
  onEnter?: (feature: maplibregl.MapGeoJSONFeature, e: maplibregl.MapMouseEvent) => void;
  /** Every evaluated move while hovered — the delegated `mousemove` equivalent. */
  onMove?: (feature: maplibregl.MapGeoJSONFeature, e: maplibregl.MapMouseEvent) => void;
  onLeave?: () => void;
}

interface HoverState {
  specs: HoverSpec[];
  over: Set<HoverSpec>;
  /** The cursor value this module set, or null when it set none. */
  cursorSet: string | null;
  pending: maplibregl.MapMouseEvent | null;
  raf: number;
}

const states = new WeakMap<maplibregl.Map, HoverState>();

function install(map: maplibregl.Map): HoverState {
  const st: HoverState = { specs: [], over: new Set(), cursorSet: null, pending: null, raf: 0 };
  states.set(map, st);
  // Coalesced to one evaluation per frame: a mouse reports faster than the
  // display refreshes, and only the last position matters.
  map.on('mousemove', (e) => {
    st.pending = e;
    if (st.raf) return;
    st.raf = requestAnimationFrame(() => {
      st.raf = 0;
      const ev = st.pending;
      st.pending = null;
      if (ev) evaluate(map, st, ev);
    });
  });
  map.on('mouseout', () => {
    st.pending = null;
    leaveAll(map, st);
  });
  return st;
}

function applyCursor(map: maplibregl.Map, st: HoverState, cursor: string | null): void {
  const canvas = map.getCanvas();
  if (cursor) {
    // Only claim the cursor when nobody else holds it (a draw tool's crosshair,
    // a drag's grabbing hand) — or when what is there is our own earlier value.
    if (canvas.style.cursor === '' || canvas.style.cursor === st.cursorSet) {
      canvas.style.cursor = cursor;
      st.cursorSet = cursor;
    }
  } else if (st.cursorSet !== null) {
    if (canvas.style.cursor === st.cursorSet) canvas.style.cursor = '';
    st.cursorSet = null;
  }
}

function leaveAll(map: maplibregl.Map, st: HoverState): void {
  for (const s of st.over) s.onLeave?.();
  st.over.clear();
  applyCursor(map, st, null);
}

function evaluate(map: maplibregl.Map, st: HoverState, e: maplibregl.MapMouseEvent): void {
  if (isMapEditing()) { leaveAll(map, st); return; }
  const active = st.specs.filter((s) => !s.when || s.when());
  const wanted = new Set<string>();
  for (const s of active) for (const id of s.layers) if (map.getLayer(id)) wanted.add(id);
  let hits: maplibregl.MapGeoJSONFeature[] = [];
  if (wanted.size) {
    try {
      hits = map.queryRenderedFeatures(e.point, { layers: [...wanted] });
    } catch {
      hits = [];   // style mid-reload — treat as nothing under the cursor
    }
  }
  // Topmost rendered feature first, so the cursor follows what the eye sees.
  let cursor: string | null = null;
  for (const f of hits) {
    const s = active.find((x) => x.cursor && x.layers.includes(f.layer.id));
    if (s) { cursor = s.cursor!; break; }
  }
  for (const s of st.specs) {
    const hit = active.includes(s)
      ? hits.find((f) => s.layers.includes(f.layer.id)) : undefined;
    if (hit) {
      if (!st.over.has(s)) {
        st.over.add(s);
        s.onEnter?.(hit, e);
      }
      s.onMove?.(hit, e);
    } else if (st.over.has(s)) {
      st.over.delete(s);
      s.onLeave?.();
    }
  }
  applyCursor(map, st, cursor);
}

/** Register a hover interest. Returns the unregister function. */
export function registerHover(map: maplibregl.Map, spec: HoverSpec): () => void {
  const st = states.get(map) ?? install(map);
  st.specs.push(spec);
  return () => {
    const i = st.specs.indexOf(spec);
    if (i >= 0) st.specs.splice(i, 1);
    if (st.over.delete(spec)) spec.onLeave?.();
  };
}
